import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import Lenis from 'lenis';

import KivoBall from '../components/KivoBall';
import heroPng from '../assets/hero png.png';
import hairAnalysisVideo from '../assets/hairanalysis.mp4';
import premiumScanVideo from '../assets/premiumscan.mp4';
import makeupAnalysisVideo from '../assets/makeupanalysis.mp4';
import productAnalysisVideo from '../assets/productanalysis.mp4';
// The clips are already full iPhone mockups (device bezel + dynamic island baked
// in) on a grey backdrop, so they render directly — no separate PNG frame — with
// a corner radius that clips the grey and follows the device's rounded corners.
import {
  FAQ_ITEMS,
  FEATURES,
  FOOTER_LINKS,
  HERO_CTA_LABEL,
  HERO_CTA_TO,
  PRICE_TIERS,
  PRIVACY_CARDS,
  PROBLEM_CARDS,
  STATS,
  VIDEO_STEPS,
  type Billing,
  type StackItem,
} from './LandingPageNew.data';

/* ── Shared helpers ──────────────────────────────────────── */

function ignoreAutoplayFailure(): void {
  /* autoplay blocked by browser policy */
}

const KIVO_EASE: [number, number, number, number] = [0.22, 0.61, 0.36, 1];

const MOBILE_BP = 640;

/**
 * Single source of truth for the videos-section local-progress timeline.
 * Both the opacity crossfade (in ActIV_Videos) and the travelling orb's
 * four resting points read from this map, so they stay in lockstep.
 *
 *   0.00 .. aArrive  — orb approaches videosA
 *   aArrive .. aLeave — orb dwells at videosA, video 1 fully visible
 *   aLeave .. bArrive — crossfade V1→V2, orb moves A → B
 *   bArrive .. bLeave — orb dwells at videosB, video 2 fully visible
 *   bLeave .. cArrive — crossfade V2→V3, orb moves B → C
 *   cArrive .. cLeave — orb dwells at videosC, video 3 fully visible
 *   cLeave .. dArrive — crossfade V3→V4, orb moves C → D
 *   dArrive .. 1.00   — orb dwells at videosD, video 4 fully visible
 */
const VIDEO_LOCAL = {
  aArrive: 0.08,
  aLeave:  0.20,
  bArrive: 0.32,
  bLeave:  0.44,
  cArrive: 0.56,
  cLeave:  0.68,
  dArrive: 0.80,
} as const;

function useViewportSize() {
  const [size, setSize] = useState(() => ({
    w: typeof window === 'undefined' ? 1280 : window.innerWidth,
    h: typeof window === 'undefined' ? 800 : window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () => { setSize({ w: window.innerWidth, h: window.innerHeight }); };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); };
  }, []);
  return size;
}

type BallState = 'idle' | 'thinking' | 'tool_calling' | 'streaming' | 'done';

const clampUnit = (v: number, lo = 0.02, hi = 0.98) => Math.max(lo, Math.min(hi, v));

function cardVariantClass(variant: StackItem['variant']): string {
  if (variant === 'deep') return 'card card--deep';
  if (variant === 'sand') return 'card card--sand card--bordered';
  return 'card card--bordered';
}

/* ── Primitive: RevealOnScroll ───────────────────────────── */

interface RevealProps {
  children: React.ReactNode;
  delay?: number;
  y?: number;
  className?: string;
}
const RevealOnScroll: React.FC<RevealProps> = ({ children, delay = 0, y = 24, className }) => {
  const reduce = useReducedMotion();
  if (reduce) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div
      data-reveal
      className={className}
      initial={{ opacity: 0, y, filter: 'blur(8px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: '0px 0px -10% 0px' }}
      transition={{ duration: 0.64, delay, ease: KIVO_EASE }}
    >
      {children}
    </motion.div>
  );
};

/* ── Glass nav ───────────────────────────────────────────── */

const GlassNav: React.FC = () => (
  <nav className="glass-nav" aria-label="Top">
    <div className="glass-nav__inner">
      <Link to="/" className="glass-nav__brand">
        kivo<b>.skin</b>
      </Link>
      <div className="glass-nav__actions">
        <Link to="/login" className="btn btn--ghost btn--sm">
          Log in
        </Link>
        <Link to={HERO_CTA_TO} className="btn btn--primary btn--sm">
          Scan free
        </Link>
      </div>
    </div>
  </nav>
);

/* ── Travelling orb (scroll-driven, sections 1→2→3→exit) ── */

interface TravellingOrbProps {
  problemRef: React.RefObject<HTMLDivElement>;
  videosRef: React.RefObject<HTMLDivElement>;
  heroAnchorRef: React.RefObject<HTMLImageElement>;
}

const TravellingOrb: React.FC<TravellingOrbProps> = ({
  problemRef,
  videosRef,
  heroAnchorRef,
}) => {
  const reduce = useReducedMotion();
  const { w, h } = useViewportSize();
  const isMobile = w < MOBILE_BP;
  const baseSize = isMobile ? 80 : 140;
  const smallScale = isMobile ? 0.85 : 0.7;

  // Use raw scroll progress — Lenis already smooths scroll, so a spring on top
  // would feel laggy ("ball waits for screen"). Tracking 1:1 keeps it natural.
  const { scrollYProgress: sp } = useScroll();

  const [stops, setStops] = useState({
    problemStart: 0.18,
    problemMid: 0.30,
    problemEnd: 0.42,
    videosStart: 0.45,
    videosEnd: 0.70,
  });

  // Live measurement of the phone image so the orb pins to its edge.
  const [heroAnchor, setHeroAnchor] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    const measure = () => {
      const total =
        document.documentElement.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      const probT = problemRef.current?.offsetTop ?? 0;
      const probH = problemRef.current?.offsetHeight ?? 0;
      const vidT = videosRef.current?.offsetTop ?? 0;
      const vidH = videosRef.current?.offsetHeight ?? 0;
      // videosEnd = the scroll point where the sticky pin releases and the
      // 3rd section's content actually starts scrolling away. That's where
      // the orb's exit slide should kick off.
      const stickyRelease = vidT + vidH - window.innerHeight;
      const probStickyRelease = probT + probH - window.innerHeight;
      setStops({
        problemStart: clampUnit(probT / total),
        problemMid: clampUnit((probT + probH * 0.45) / total),
        problemEnd: clampUnit(probStickyRelease / total, 0.02, 0.99),
        videosStart: clampUnit(vidT / total),
        videosEnd: clampUnit(stickyRelease / total, 0.02, 0.99),
      });

      // Pin the hero orb to the phone image. Store the image's position as if
      // the page were scrolled to the top — the orb's hero waypoint is only
      // active near scroll=0 anyway, and this keeps the value stable across
      // resize-while-scrolled.
      const el = heroAnchorRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        setHeroAnchor({
          left: r.left + window.scrollX,
          top: r.top + window.scrollY,
          width: r.width,
          height: r.height,
        });
      }
    };
    measure();
    const t1 = window.setTimeout(measure, 100);
    const t2 = window.setTimeout(measure, 400);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [problemRef, videosRef, heroAnchorRef]);

  const targets = isMobile
    ? {
        hero:     { fx: 0.50, fy: 0.58 },
        problem:  { fx: 0.50, fy: 0.46 },
        videosA:  { fx: 0.24, fy: 0.86 },
        videosB:  { fx: 0.44, fy: 0.92 },
        videosC:  { fx: 0.64, fy: 0.90 },
        videosD:  { fx: 0.80, fy: 0.84 },
        exit:     { fx: 1.30, fy: 0.78 },
      }
    : {
        hero:     { fx: 0.62, fy: 0.50 },
        problem:  { fx: 0.50, fy: 0.40 },
        videosA:  { fx: 0.78, fy: 0.24 },
        videosB:  { fx: 0.82, fy: 0.44 },
        videosC:  { fx: 0.82, fy: 0.64 },
        videosD:  { fx: 0.78, fy: 0.84 },
        exit:     { fx: 1.30, fy: 0.72 },
      };

  const toXY = (t: { fx: number; fy: number }) => ({
    x: w * t.fx - baseSize / 2,
    y: h * t.fy - baseSize / 2,
  });

  // Hero waypoint overrides — peek exactly half-out from behind the phone:
  // desktop pokes from the LEFT edge (vertically centred on the phone),
  // mobile pokes from the TOP edge (horizontally centred on the phone).
  const heroFromAnchor = heroAnchor
    ? isMobile
      ? {
          x: heroAnchor.left + heroAnchor.width / 2 - baseSize / 2,
          y: heroAnchor.top - baseSize / 2,
        }
      : {
          x: heroAnchor.left - baseSize / 2,
          y: heroAnchor.top + heroAnchor.height / 2 - baseSize / 2,
        }
    : null;

  const hero = heroFromAnchor ?? toXY(targets.hero);
  const problem = toXY(targets.problem);
  const vA = toXY(targets.videosA);
  const vB = toXY(targets.videosB);
  const vC = toXY(targets.videosC);
  const vD = toXY(targets.videosD);
  const exit = toXY(targets.exit);

  // Ball starts moving at 30% of the scroll distance to problemStart —
  // early enough to feel alive, late enough not to drift while reading hero.
  const heroStay = stops.problemStart * 0.30;
  const exitArrived = Math.min(0.9999, stops.videosEnd + 0.06);

  // 3-beat rhythm in the problem section:
  //   problemArrive — orb has glided to its problem-section position
  //   problemHold   — orb sits still while cards spread (matches card timeline)
  //   then it leaves toward videos.
  const problemArrive = Math.min(
    stops.problemEnd,
    stops.problemStart + Math.max(0.01, (stops.problemMid - stops.problemStart) * 0.5),
  );
  const problemHold = Math.max(
    problemArrive + 0.001,
    Math.min(stops.problemEnd - 0.01, stops.problemEnd - 0.02),
  );

  // Videos section: 3 resting points (one per video) tied to the same local
  // progress windows used by the opacity crossfade (VIDEO_LOCAL_*). The orb
  // dwells while a video plays and moves while the crossfade happens.
  const vSpan = Math.max(0.001, stops.videosEnd - stops.videosStart);
  const gVid = (local: number) => stops.videosStart + local * vSpan;
  const gA_arrive = gVid(VIDEO_LOCAL.aArrive);
  const gA_leave  = gVid(VIDEO_LOCAL.aLeave);
  const gB_arrive = gVid(VIDEO_LOCAL.bArrive);
  const gB_leave  = gVid(VIDEO_LOCAL.bLeave);
  const gC_arrive = gVid(VIDEO_LOCAL.cArrive);
  const gC_leave  = gVid(VIDEO_LOCAL.cLeave);
  const gD_arrive = gVid(VIDEO_LOCAL.dArrive);

  const rawInput = [
    0,
    heroStay,
    problemArrive,
    problemHold,
    gA_arrive,
    gA_leave,
    gB_arrive,
    gB_leave,
    gC_arrive,
    gC_leave,
    gD_arrive,
    stops.videosEnd,
    Math.max(stops.videosEnd + 0.001, exitArrived),
    1,
  ];
  // Ensure strictly monotonic input for useTransform (uses running max, not raw).
  const input: number[] = [];
  for (const [i, element] of rawInput.entries()) {
    input.push(i === 0 ? element : Math.max(element, input[i - 1] + 0.0005));
  }

  const xOut = [hero.x, hero.x, problem.x, problem.x, vA.x, vA.x, vB.x, vB.x, vC.x, vC.x, vD.x, vD.x, exit.x, exit.x];
  const yOut = [hero.y, hero.y, problem.y, problem.y, vA.y, vA.y, vB.y, vB.y, vC.y, vC.y, vD.y, vD.y, exit.y, exit.y];
  const sOut = [
    1, 1,
    smallScale, smallScale,
    smallScale, smallScale,
    smallScale, smallScale,
    smallScale, smallScale,
    smallScale, smallScale,
    smallScale, smallScale,
  ];

  const fadeStart = stops.videosEnd;
  const fadeEnd = Math.min(0.9998, stops.videosEnd + 0.09);
  const opInput = [0, fadeStart, Math.max(fadeStart + 0.001, fadeEnd), 1];
  const opOut = [1, 1, 0, 0];

  const x = useTransform(sp, input, xOut, { clamp: true });
  const y = useTransform(sp, input, yOut, { clamp: true });
  const scale = useTransform(sp, input, sOut, { clamp: true });
  const opacity = useTransform(sp, opInput, opOut, { clamp: true });

  const [orbState, setOrbState] = useState<BallState>('idle');
  useMotionValueEvent(sp, 'change', (v) => {
    let next: BallState = 'idle';
    if (v < heroStay)             next = 'idle';
    else if (v < gA_arrive)       next = 'thinking';
    else if (v < stops.videosEnd) next = 'streaming';
    else                          next = 'idle';
    setOrbState((prev) => (prev === next ? prev : next));
  });

  if (reduce) return null;

  return (
    <motion.div
      className="travelling-orb"
      style={{ x, y, scale, opacity, width: baseSize, height: baseSize }}
      aria-hidden
    >
      <KivoBall size={baseSize} state={orbState} />
    </motion.div>
  );
};

/* ── Problem section (scroll-driven: stacked → fanned readable) ── */

const ProblemCardStatic: React.FC<{ item: StackItem }> = ({ item }) => {
  const variantClass = cardVariantClass(item.variant);
  const pillClass = item.variant === 'deep' ? 'pill pill--teal bg-white/15 text-white' : 'pill pill--teal';
  return (
    <div className={`${variantClass} problem-card stack-3`}>
      <span className={pillClass}>{item.pill}</span>
      <h3 className="t-h3">{item.title}</h3>
      <p className="t-body">{item.body}</p>
    </div>
  );
};

const StackedProblemCard: React.FC<{
  item: StackItem;
  index: number;
  total: number;
  progress: MotionValue<number>;
  spreadX: number;
  spreadY: number;
}> = ({ item, index, total, progress, spreadX, spreadY }) => {
  const center = (total - 1) / 2;
  const offset = index - center;

  // Stacked rest state — cards overlap with a small fan so all edges peek.
  const stackedX = offset * 18;
  const stackedY = Math.abs(offset) * 14;
  const stackedRot = offset * -4;

  // Spread target — fully separated, no rotation, all text readable.
  const spreadDx = offset * spreadX;
  const spreadDy = offset * spreadY;

  const x = useTransform(
    progress,
    [0, 0.18, 0.78, 1],
    [stackedX, stackedX, spreadDx, spreadDx],
  );
  const y = useTransform(
    progress,
    [0, 0.18, 0.78, 1],
    [stackedY, stackedY, spreadDy, spreadDy],
  );
  const rotate = useTransform(
    progress,
    [0, 0.18, 0.78, 1],
    [stackedRot, stackedRot, 0, 0],
  );

  const variantClass = cardVariantClass(item.variant);
  const pillClass = item.variant === 'deep' ? 'pill pill--teal bg-white/15 text-white' : 'pill pill--teal';
  return (
    <motion.div
      className={`stacked-card ${variantClass}`}
      style={{ x, y, rotate, zIndex: total - Math.abs(offset) }}
    >
      <div className="stack-3">
        <span className={pillClass}>{item.pill}</span>
        <h3 className="t-h3">{item.title}</h3>
        <p className="t-body">{item.body}</p>
      </div>
    </motion.div>
  );
};

/* ── Hero ────────────────────────────────────────────────── */

const HeroProductMock = React.forwardRef<HTMLImageElement>((_, ref) => (
  <img
    ref={ref}
    src={heroPng}
    alt="kivo skin analysis"
    style={{
      display: 'block',
      width: 'min(320px, 68vw)',
      height: 'auto',
      filter: 'drop-shadow(0 32px 64px color-mix(in srgb, var(--c-charcoal) 20%, transparent))',
    }}
  />
));
HeroProductMock.displayName = 'HeroProductMock';

const ActIHero: React.FC<{ heroAnchorRef: React.RefObject<HTMLImageElement> }> = ({
  heroAnchorRef,
}) => (
  <section className="lp-hero" aria-label="Hero">
    <div className="hero-blob hero-blob--tl" aria-hidden />
    <div className="hero-blob hero-blob--br" aria-hidden />
    <div className="hero-grain" aria-hidden />
    <div className="lp-hero__inner lp-hero__inner--split">
      <div className="lp-hero__copy">
        <RevealOnScroll>
          <h1 className="lp-hero__title">
            your skin, <em>finally explained</em> — in under a minute.
          </h1>
        </RevealOnScroll>
        <RevealOnScroll delay={0.1}>
          <p className="lp-hero__sub">
            scan from a few angles. get plain-english insights and a routine built for <em>your</em>{' '}
            skin — not the algorithm&apos;s average.
          </p>
        </RevealOnScroll>
        <RevealOnScroll delay={0.18}>
          <div className="lp-hero__cta lp-hero__cta--single">
            <Link to={HERO_CTA_TO} className="btn btn--primary btn--lg">
              {HERO_CTA_LABEL}
            </Link>
            <a href="#how-it-works" className="lp-hero__link">
              or see how it works ↓
            </a>
          </div>
        </RevealOnScroll>
        <RevealOnScroll delay={0.22}>
          <p className="t-small t-mute">
            For cosmetic & educational use only. Not a medical service.
          </p>
        </RevealOnScroll>
        <RevealOnScroll delay={0.26}>
          <p className="lp-hero__trust">
            no credit card · takes 60 seconds · your scan stays private
          </p>
        </RevealOnScroll>
      </div>
      <div className="lp-hero__visual">
        <RevealOnScroll delay={0.18} y={32}>
          <HeroProductMock ref={heroAnchorRef} />
        </RevealOnScroll>
      </div>
    </div>
  </section>
);

/* ── Trust bar ───────────────────────────────────────────── */

const PressTrustBar: React.FC = () => (
  <section className="lp-trust-bar" aria-label="Credibility">
    <div className="lp-trust-bar__inner">
      <RevealOnScroll>
        <p className="lp-trust-bar__line">
          <span className="lp-trust-bar__tag">credibility</span>
          every score is traceable · every ingredient is explained · privacy-first by design
        </p>
      </RevealOnScroll>
    </div>
  </section>
);

/* ── Problem (sticky card stack) ─────────────────────────── */

const ProblemHead: React.FC = () => (
  <div className="lp-section__head stack-3 lp-problem__head--flow">
    <RevealOnScroll>
      <span className="pill pill--teal">the problem</span>
    </RevealOnScroll>
    <RevealOnScroll delay={0.08}>
      <h2 className="t-h2">
        skincare <em>wasn&apos;t built</em> for your face.
      </h2>
    </RevealOnScroll>
    <RevealOnScroll delay={0.16}>
      <p className="t-body t-muted">
        you&apos;ve tried the routines. you&apos;ve read the labels. nothing actually fits{' '}
        <em>you</em>. that&apos;s not your fault — it&apos;s the category&apos;s.
      </p>
    </RevealOnScroll>
  </div>
);

const ActIIIProblem: React.FC<{
  sectionRef: React.RefObject<HTMLDivElement>;
}> = ({ sectionRef }) => {
  const reduce = useReducedMotion();
  const { w, h } = useViewportSize();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });
  const smooth = useSpring(scrollYProgress, {
    stiffness: 110,
    damping: 28,
    mass: 0.35,
  });

  // Reduced-motion + small screens fall back to a static stacked-look grid
  // so all card text remains readable without scroll.
  if (reduce) {
    return (
      <section ref={sectionRef} className="lp-section lp-problem" aria-label="The problem">
        <div className="lp-section__inner">
          <ProblemHead />
          <div className="lp-problem__grid">
            {PROBLEM_CARDS.map((it, i) => (
              <ProblemCardStatic key={i} item={it} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Card spread distances tuned by viewport. On wide screens cards fan out
  // horizontally into a 3-column read. On narrow screens they fan vertically;
  // the mobile spread is bounded by half-stage-height minus half-card-height
  // (~140px) so the top + bottom cards aren't clipped by the sticky overflow.
  const isWide = w >= 900;
  const spreadX = isWide ? Math.min(380, w * 0.28) : 0;
  const spreadY = isWide ? 0 : Math.min(150, h * 0.16);

  return (
    <section
      ref={sectionRef}
      className="lp-problem-scene"
      style={{ height: '240vh' }}
      aria-label="The problem"
    >
      <div className="lp-problem-sticky">
        <div className="lp-problem-sticky__inner">
          <ProblemHead />
          <div className="lp-problem__stage">
            {PROBLEM_CARDS.map((it, i) => (
              <StackedProblemCard
                key={i}
                item={it}
                index={i}
                total={PROBLEM_CARDS.length}
                progress={smooth}
                spreadX={spreadX}
                spreadY={spreadY}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── How it works (videos) ───────────────────────────────── */

const VIDEO_SOURCES = [hairAnalysisVideo, premiumScanVideo, makeupAnalysisVideo, productAnalysisVideo];

const KivoVideo = React.forwardRef<
  HTMLVideoElement,
  { src: string; label: string }
>(({ src, label }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const combinedRef = (el: HTMLVideoElement | null) => {
    (videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref).current = el;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        const vid = videoRef.current;
        if (!vid) return;
        if (entry.isIntersecting) {
          if (!vid.src) vid.src = src;
          vid.play().catch(ignoreAutoplayFailure);
        } else {
          vid.pause();
        }
      },
      { rootMargin: '200px' }
    );
    obs.observe(container);
    return () => { obs.disconnect(); };
  }, [src]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        // The clip is a full iPhone mockup on a grey backdrop; this radius (in %
        // of the 520×1080 frame) clips that grey and follows the device's own
        // rounded corners. overflow:hidden so nothing spills past the curve.
        borderRadius: '15.5% / 8.1%',
        overflow: 'hidden',
        boxShadow: '0 18px 50px rgba(31, 36, 33, 0.18)',
      }}
    >
      <video
        ref={combinedRef}
        loop
        muted
        playsInline
        preload="none"
        aria-label={label}
        style={{ width: '100%', height: 'auto', display: 'block' }}
      />
    </div>
  );
});
KivoVideo.displayName = 'KivoVideo';

const ActIVVideos: React.FC<{ sectionRef: React.RefObject<HTMLDivElement> }> = ({
  sectionRef,
}) => {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });
  const smooth = useSpring(scrollYProgress, { stiffness: 100, damping: 30, mass: 0.3 });

  // Opacity timelines stay in lockstep with VIDEO_LOCAL so the orb's resting
  // points and the video crossfades line up perfectly.
  const op1 = useTransform(
    smooth,
    [0.0, 0.04, VIDEO_LOCAL.aLeave, VIDEO_LOCAL.bArrive],
    [1, 1, 1, 0],
  );
  const op2 = useTransform(
    smooth,
    [VIDEO_LOCAL.aLeave, VIDEO_LOCAL.bArrive, VIDEO_LOCAL.bLeave, VIDEO_LOCAL.cArrive],
    [0, 1, 1, 0],
  );
  const op3 = useTransform(
    smooth,
    [VIDEO_LOCAL.bLeave, VIDEO_LOCAL.cArrive, VIDEO_LOCAL.cLeave, VIDEO_LOCAL.dArrive],
    [0, 1, 1, 0],
  );
  const op4 = useTransform(
    smooth,
    [VIDEO_LOCAL.cLeave, VIDEO_LOCAL.dArrive, 1.0, 1.0],
    [0, 1, 1, 1],
  );
  const ops = [op1, op2, op3, op4];

  // Restart each video from the start when it becomes the active one
  // (opacity crosses 0.5 upward). Watching the motion values means we react
  // to scroll direction in both directions without an extra index state.
  const videoRefs = [
    useRef<HTMLVideoElement>(null),
    useRef<HTMLVideoElement>(null),
    useRef<HTMLVideoElement>(null),
    useRef<HTMLVideoElement>(null),
  ];
  const lastOps = useRef<[number, number, number, number]>([1, 0, 0, 0]);
  const restartFor = (i: number) => (v: number) => {
    const prev = lastOps.current[i];
    if (prev < 0.5 && v >= 0.5) {
      const el = videoRefs[i].current;
      if (el) {
        try { el.currentTime = 0; } catch { /* seek unsupported */ }
        el.play().catch(ignoreAutoplayFailure);
      }
    }
    lastOps.current[i] = v;
  };
  useMotionValueEvent(op1, 'change', restartFor(0));
  useMotionValueEvent(op2, 'change', restartFor(1));
  useMotionValueEvent(op3, 'change', restartFor(2));
  useMotionValueEvent(op4, 'change', restartFor(3));

  if (reduce) {
    return (
      <section
        ref={sectionRef}
        id="how-it-works"
        className="lp-videos-section lp-videos-section--reduced"
        aria-label="How it works"
      >
        <div className="lp-videos-section__inner">
          <div className="lp-section__head stack-3">
            <span className="pill pill--teal">how it works</span>
            <h2 className="t-h2">four steps. one <em>honest</em> read.</h2>
          </div>
          {VIDEO_STEPS.map((s, i) => (
            <div key={i} className="lp-videos__pair">
              <div style={{ width: '100%', maxWidth: 260 }}>
                <KivoVideo src={VIDEO_SOURCES[i]} label={s.pill} />
              </div>
              <div className="lp-videos__copy-block">
                <div className="lp-step-label">
                  <span className="lp-step-label__num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="lp-step-label__bar" aria-hidden="true" />
                  <span className="lp-step-label__text">{s.pill}</span>
                  {s.alpha && <span className="pill-tag pill-tag--alpha">alpha</span>}
                </div>
                <h3 className="t-h2">{s.title}</h3>
                <p className="t-body t-muted">{s.body}</p>
              </div>
            </div>
          ))}
          <div>
            <Link to={HERO_CTA_TO} className="btn btn--primary">
              try my scan — free
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      className="scene-scroll lp-videos-section"
      style={{ height: '640vh' }}
      aria-label="How it works"
    >
      <div className="scene-sticky lp-videos-sticky">
        <div className="lp-videos">
          <div className="lp-videos__stage">
            {VIDEO_STEPS.map((s, i) => (
              <motion.div key={i} className="lp-videos__slot" style={{ opacity: ops[i] }}>
                <KivoVideo ref={videoRefs[i]} src={VIDEO_SOURCES[i]} label={s.pill} />
              </motion.div>
            ))}
          </div>
          <div className="lp-videos__copy">
            {VIDEO_STEPS.map((s, i) => (
              <motion.div
                key={i}
                className="lp-videos__copy-slot"
                style={{ opacity: ops[i] }}
              >
                <div className="lp-step-label">
                  <span className="lp-step-label__num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="lp-step-label__bar" aria-hidden="true" />
                  <span className="lp-step-label__text">{s.pill}</span>
                  {s.alpha && <span className="pill-tag pill-tag--alpha">alpha</span>}
                </div>
                <h3 className="t-h2">{s.title}</h3>
                <p className="t-body t-muted">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Stats ───────────────────────────────────────────────── */

const ActVStats: React.FC = () => (
  <section className="lp-section" aria-label="What kivo actually does">
    <div className="lp-section__inner">
      <div className="lp-section__head stack-3">
        <RevealOnScroll>
          <span className="pill pill--teal">built honest</span>
        </RevealOnScroll>
        <RevealOnScroll delay={0.06}>
          <h2 className="t-h2">every number here is something we <em>actually</em> do.</h2>
        </RevealOnScroll>
      </div>
      <div className="g-3">
        {STATS.map((s, i) => (
          <RevealOnScroll key={i} delay={i * 0.08}>
            <div className="stat-tile card card--sand card--bordered">
              <span className="stat-tile__num">{s.num}</span>
              <span className="stat-tile__label">{s.label}</span>
              <p className="stat-tile__context t-small t-muted">{s.context}</p>
            </div>
          </RevealOnScroll>
        ))}
      </div>
    </div>
  </section>
);

/* ── Features + Trust (combined, asymmetric layout) ──────── */

const FeaturesAndTrust: React.FC = () => {
  const hero = FEATURES[0];
  const minor = FEATURES.slice(1);
  return (
    <section className="lp-section lp-ft" aria-label="What kivo does and how we handle it">
      <div className="lp-section__inner">
        <div className="lp-section__head stack-3">
          <RevealOnScroll>
            <span className="pill pill--teal">what kivo does</span>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="t-h2">everything your scan can <em>actually</em> tell you.</h2>
          </RevealOnScroll>
        </div>

        <div className="lp-ft__grid">
          <RevealOnScroll className="lp-ft__hero-wrap">
            <article className="lp-ft__hero">
              <span className="lp-ft__hero-icon" aria-hidden>
                {hero.icon}
              </span>
              <h3 className="lp-ft__hero-title">{hero.title}</h3>
              <p className="lp-ft__hero-body">{hero.body}</p>
              <div className="lp-ft__hero-waves" aria-hidden />
            </article>
          </RevealOnScroll>

          <div className="lp-ft__minor">
            {minor.map((f, i) => (
              <RevealOnScroll key={i} delay={i * 0.05}>
                <article className="lp-ft__feature">
                  <span className="lp-ft__feature-icon" aria-hidden>
                    {f.icon}
                  </span>
                  <h4 className="lp-ft__feature-title">{f.title}</h4>
                  <p className="lp-ft__feature-body">{f.body}</p>
                </article>
              </RevealOnScroll>
            ))}
          </div>
        </div>

        <div className="lp-ft__trust">
          <div className="lp-ft__trust-head">
            <RevealOnScroll>
              <span className="lp-ft__trust-tag">the fine print, up front</span>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <p className="lp-ft__trust-lede">
                we&apos;ll tell you <em>what we are</em>. and what we aren&apos;t.
              </p>
            </RevealOnScroll>
          </div>
          <div className="lp-ft__trust-items">
            {PRIVACY_CARDS.map((c, i) => (
              <RevealOnScroll key={i} delay={i * 0.05}>
                <div className="lp-ft__trust-item">
                  <h5 className="lp-ft__trust-title">{c.title}</h5>
                  <p className="lp-ft__trust-body">{c.body}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Pricing ─────────────────────────────────────────────── */

const ActVIIPricing: React.FC = () => {
  const [billing, setBilling] = useState<Billing>('yearly');

  return (
    <section className="lp-section" aria-label="Plans">
      <div className="lp-section__inner">
        <div className="lp-section__head stack-3">
          <RevealOnScroll>
            <span className="pill pill--teal">plans</span>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="t-h2">start free. go <em>deeper</em> when you&apos;re ready.</h2>
          </RevealOnScroll>
        </div>

        <div className="lp-pricing__toggle-wrap" role="radiogroup" aria-label="Billing period">
          <div className="lp-pricing__toggle">
            <button
              type="button"
              role="radio"
              aria-checked={billing === 'monthly'}
              className={`lp-pricing__toggle-btn${billing === 'monthly' ? ' is-active' : ''}`}
              onClick={() => { setBilling('monthly'); }}
            >
              monthly
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={billing === 'yearly'}
              className={`lp-pricing__toggle-btn${billing === 'yearly' ? ' is-active' : ''}`}
              onClick={() => { setBilling('yearly'); }}
            >
              yearly · save 33%
            </button>
          </div>
        </div>

        <div className="lp-pricing">
          {PRICE_TIERS.map((t, i) => {
            const popularClass = t.popular ? ' price-card--popular' : '';
            return (
              <RevealOnScroll key={i} delay={i * 0.08}>
                <div className={`price-card ${cardVariantClass(t.variant)}${popularClass}`}>
                  {t.popular && <span className="price-card__ribbon">most popular</span>}
                  <div className="stack-2">
                    <span className="t-label">{t.name}</span>
                    <span className="price-card__amt">
                      {billing === 'monthly' ? t.monthly : t.yearly}
                    </span>
                    <span className="t-small">
                      {t.desc}
                      {billing === 'yearly' && t.yearlyAnnualNote && (
                        <>
                          {'  ·  '}
                          <span className="price-card__note">{t.yearlyAnnualNote}</span>
                        </>
                      )}
                    </span>
                  </div>
                  <ul>
                    {t.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                  <Link
                    to={t.to}
                    className={
                      t.variant === 'deep'
                        ? 'btn btn--cream btn--full'
                        : 'btn btn--primary btn--full'
                    }
                  >
                    {t.cta}
                  </Link>
                </div>
              </RevealOnScroll>
            );
          })}
        </div>

        <RevealOnScroll>
          <p className="lp-pricing__fineprint">
            cancel anytime · no hidden fees · all prices in USD
          </p>
        </RevealOnScroll>
      </div>
    </section>
  );
};

/* ── FAQ ─────────────────────────────────────────────────── */

const FAQ: React.FC = () => (
  <section className="lp-section lp-faq-section" aria-label="Frequently asked questions">
    <div className="lp-section__inner lp-faq-section__inner">
      <div className="lp-faq-section__head">
        <RevealOnScroll>
          <span className="pill pill--teal">faq</span>
        </RevealOnScroll>
        <RevealOnScroll delay={0.06}>
          <h2 className="lp-faq-section__title">
            questions, <em>asked plainly.</em>
          </h2>
        </RevealOnScroll>
        <RevealOnScroll delay={0.12}>
          <p className="lp-faq-section__lede">
            short answers. no marketing. if something we say here doesn&apos;t match what you see in the
            app — tell us.
          </p>
        </RevealOnScroll>
      </div>
      <div className="lp-faq">
        {FAQ_ITEMS.map((item, i) => (
          <RevealOnScroll key={i} delay={i * 0.03}>
            <details className="lp-faq__item">
              <summary className="lp-faq__q">
                <span className="lp-faq__num">{String(i + 1).padStart(2, '0')}</span>
                <span className="lp-faq__q-text">{item.q}</span>
                <span className="lp-faq__chev" aria-hidden />
              </summary>
              <p className="lp-faq__a">{item.a}</p>
            </details>
          </RevealOnScroll>
        ))}
      </div>
    </div>
  </section>
);

/* ── Closing CTA (with orb) ──────────────────────────────── */

const ActVIIIClosingCTA: React.FC = () => (
  <section className="lp-section" aria-label="Get started">
    <div className="lp-section__inner">
      <RevealOnScroll>
        <div className="lp-closing scan-pattern--cream">
          <h2 className="lp-closing__title">
            your skin, <em>finally</em>, in plain language.
          </h2>
          <p className="lp-closing__sub">
            60 seconds. no credit card. one honest read of what your skin actually needs. start
            where you are.
          </p>
          <div className="lp-closing__cta lp-closing__cta--single">
            <Link to={HERO_CTA_TO} className="btn btn--cream btn--lg">
              {HERO_CTA_LABEL}
            </Link>
          </div>
        </div>
      </RevealOnScroll>
    </div>
  </section>
);

/* ── Footer ──────────────────────────────────────────────── */

const SiteFooter: React.FC = () => (
  <footer className="lp-foot scan-pattern--cream">
    <div className="lp-foot__inner">
      <div className="lp-foot__row">
        <p className="lp-foot__big">
          begin where
          <br />
          you <em>are.</em>
        </p>
        <nav className="lp-foot__links" aria-label="Footer navigation">
          {FOOTER_LINKS.map((l) => (
            <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer">
              {l.label}
            </a>
          ))}
        </nav>
      </div>
      <hr className="rule rule--cream lp-foot__rule" />
      <div className="lp-foot__row lp-foot__meta">
        <span>© {new Date().getFullYear()} kivo.skin</span>
        <span>privacy-first · EU hosted</span>
      </div>
    </div>
  </footer>
);

/* ── Page ────────────────────────────────────────────────── */

const BottomBlurOverlay: React.FC = () => <div className="bottom-blur" aria-hidden />;

const LandingPageNew: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();

  // Section refs measured by the travelling orb.
  const problemRef = useRef<HTMLDivElement>(null);
  const videosRef = useRef<HTMLDivElement>(null);
  const heroAnchorRef = useRef<HTMLImageElement>(null);

  // Scope body class for CSS overrides.
  useEffect(() => {
    document.body.classList.add('landing-new');
    return () => {
      document.body.classList.remove('landing-new');
      document.body.classList.remove('lenis-active');
    };
  }, []);

  // Lenis smooth scroll (skipped under reduced-motion).
  useEffect(() => {
    if (prefersReducedMotion) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.4,
    });
    document.body.classList.add('lenis-active');
    let raf = 0;
    const tick = (time: number) => {
      lenis.raf(time);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      document.body.classList.remove('lenis-active');
    };
  }, [prefersReducedMotion]);

  return (
    <div className="landing-new-root">
      <GlassNav />
      <TravellingOrb
        problemRef={problemRef}
        videosRef={videosRef}
        heroAnchorRef={heroAnchorRef}
      />

      <ActIHero heroAnchorRef={heroAnchorRef} />
      <PressTrustBar />
      <ActIIIProblem sectionRef={problemRef} />
      <ActIVVideos sectionRef={videosRef} />
      <ActVStats />
      <FeaturesAndTrust />
      <ActVIIPricing />
      <FAQ />
      <ActVIIIClosingCTA />
      <SiteFooter />

      <BottomBlurOverlay />
    </div>
  );
};

export default LandingPageNew;
